import { useEffect, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Customers = lazy(() => import("@/pages/Customers"));
const CustomerProgress = lazy(() => import("@/pages/CustomerProgress"));
const Reactivation = lazy(() => import("@/pages/Reactivation"));
const Settings = lazy(() => import("@/pages/Settings"));
const Support = lazy(() => import("@/pages/Support"));
const SupportWhatsApp = lazy(() => import("@/pages/SupportWhatsApp"));
const CustomerDetail = lazy(() => import("@/pages/CustomerDetail"));
const Salesfunnel = lazy(() => import("@/pages/Salesfunnel"));
const LeadsList = lazy(() => import("@/pages/LeadsList"));
const LeadContactDetail = lazy(() => import("@/pages/LeadContactDetail"));
const DealsList = lazy(() => import("@/pages/DealsList"));
const DealDetail = lazy(() => import("@/pages/DealDetail"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const Schatkaart = lazy(() => import("@/pages/Schatkaart"));
const Rapportage = lazy(() => import("@/pages/Rapportage"));
const WebinarStats = lazy(() => import("@/pages/WebinarStats"));
const WebinarCallsDrilldown = lazy(() => import("@/pages/WebinarCallsDrilldown"));
const TeamDashboard = lazy(() => import("@/pages/TeamDashboard"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminEvents = lazy(() => import("@/pages/AdminEvents"));
const AdminWorkflows = lazy(() => import("@/pages/AdminWorkflows"));
const Specificatie = lazy(() => import("@/pages/Specificatie"));
const TvDashboard = lazy(() => import("@/pages/TvDashboard"));
const Duplicates = lazy(() => import("@/pages/Duplicates"));
const WebinarRegistrations = lazy(() => import("@/pages/WebinarRegistrations"));
const Suggestions = lazy(() => import("@/pages/Suggestions"));
const FathomOverview = lazy(() => import("@/pages/FathomOverview"));
const WebinarSendHistory = lazy(() => import("@/pages/WebinarSendHistory"));
const SalesStats = lazy(() => import("@/pages/SalesStats"));
const Uitstroom = lazy(() => import("@/pages/Uitstroom"));
const RapportageOmzet = lazy(() => import("@/pages/RapportageOmzet"));
const Handleiding = lazy(() => import("@/pages/Handleiding"));
const DesignSketches = lazy(() => import("@/pages/DesignSketches"));
const Processen = lazy(() => import("@/pages/Processen"));
const Backoffice = lazy(() => import("@/pages/Backoffice"));
const BackofficeNieuweKlanten = lazy(() => import("@/pages/BackofficeNieuweKlanten"));
const BackofficeAbonnementen = lazy(() => import("@/pages/BackofficeAbonnementen"));
const BackofficeRefunds = lazy(() => import("@/pages/BackofficeRefunds"));
const BackofficeLtv = lazy(() => import("@/pages/BackofficeLtv"));
const BackofficeEvents = lazy(() => import("@/pages/BackofficeEvents"));
const Logboek = lazy(() => import("@/pages/Logboek"));
const Cockpit = lazy(() => import("@/pages/Cockpit"));
const KlantenV2 = lazy(() => import("@/pages/KlantenV2"));
const KlantenV2Detail = lazy(() => import("@/pages/KlantenV2Detail"));
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-700" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/tv" element={<ProtectedRoute><TvDashboard /></ProtectedRoute>} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="dashboard/:tab" element={<Dashboard />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/progress" element={<CustomerProgress />} />
        <Route path="customers/reactivation" element={<Reactivation />} />
        <Route path="settings" element={<Settings />} />
        <Route path="rapportage" element={<Rapportage />} />
        <Route path="rapportage/omzet" element={<RapportageOmzet />} />
        <Route path="rapportage/webinar" element={<WebinarStats />} />
        <Route path="rapportage/uitstroom" element={<Uitstroom />} />
        <Route path="handleiding" element={<Handleiding />} />
        <Route path="ontwerp/klantkaart" element={<DesignSketches />} />
        <Route path="processen" element={<Processen />} />
        <Route path="processen/:slug" element={<Processen />} />
        <Route path="rapportage/webinar/calls/:broadcastId" element={<WebinarCallsDrilldown />} />
        <Route path="customers/:id" element={<CustomerDetail />} />
        <Route path="leads" element={<Navigate to="/sales/leads" replace />} />
        <Route path="sales" element={<Navigate to="/sales/funnel" replace />} />
        <Route path="sales/funnel" element={<Salesfunnel />} />
        <Route path="sales/statistieken" element={<SalesStats />} />
        <Route path="sales/leads" element={<LeadsList />} />
        <Route path="sales/leads/:id" element={<LeadContactDetail />} />
        <Route path="sales/deals" element={<DealsList />} />
        <Route path="sales/webinars" element={<WebinarRegistrations />} />
        <Route path="sales/suggesties" element={<Navigate to="/backoffice/suggesties" replace />} />
        <Route path="backoffice/suggesties" element={<Suggestions />} />
        <Route path="backoffice/fathom" element={<FathomOverview />} />
        <Route path="sales/webinars/verzendingen" element={<WebinarSendHistory />} />
        <Route path="backoffice/nieuwe-klanten" element={<BackofficeNieuweKlanten />} />
        <Route path="backoffice/achterstanden" element={<Backoffice />} />
        <Route path="backoffice/abonnementen" element={<BackofficeAbonnementen />} />
        <Route path="backoffice/refunds" element={<BackofficeRefunds />} />
        <Route path="backoffice/ltv" element={<BackofficeLtv />} />
        <Route path="backoffice/events" element={<BackofficeEvents />} />
        <Route path="sales/deals/:id" element={<DealDetail />} />
        <Route path="schatkaart" element={<Schatkaart />} />
        <Route path="support" element={<Navigate to="/support/inbox" replace />} />
        <Route path="support/whatsapp" element={<SupportWhatsApp />} />
        <Route path="support/:view" element={<Support />} />
        <Route path="team" element={<TeamDashboard />} />
        <Route path="duplicaten" element={<Duplicates />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="logboek" element={<Logboek />} />
        <Route path="cockpit" element={<Cockpit />} />
        <Route path="klanten-v2" element={<KlantenV2 />} />
        <Route path="klanten-v2/:id" element={<KlantenV2Detail />} />
        <Route path="admin" element={<Admin />} />
        <Route path="admin/events" element={<AdminEvents />} />
        <Route path="admin/workflows" element={<AdminWorkflows />} />
        <Route path="admin/specificatie" element={<Specificatie />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
